import { useState } from 'react';
import type { IndicadorMedicao } from '../../api/scorecards.js';

interface SerieHistoricaProps {
  medicoes: IndicadorMedicao[];
  meta: number;
  baseline: number | null;
}

const LARGURA = 280;
const ALTURA = 64;
const PADDING = 8;

// Sparkline de série única (mudança ao longo do tempo) — uma cor
// sequencial (--cor-aco), sem paleta categórica (dataviz: "single series
// needs no legend box"). Linha 2px, sem eixo pesado, crosshair simples no
// hover.
export function SerieHistorica({ medicoes, meta, baseline }: SerieHistoricaProps) {
  const [indiceHover, setIndiceHover] = useState<number | null>(null);

  if (medicoes.length === 0) {
    return <p className="text-xs text-grafite">Sem histórico de medições ainda.</p>;
  }

  const valores = medicoes.map((m) => m.valor);
  const todosValores = [...valores, meta, ...(baseline !== null ? [baseline] : [])];
  const min = Math.min(...todosValores);
  const max = Math.max(...todosValores);
  const escalaY = (v: number) => ALTURA - PADDING - ((v - min) / (max - min || 1)) * (ALTURA - PADDING * 2);
  const escalaX = (i: number) => PADDING + (i / Math.max(1, medicoes.length - 1)) * (LARGURA - PADDING * 2);

  const pontos = medicoes.map((m, i) => `${escalaX(i)},${escalaY(m.valor)}`).join(' ');
  const yMeta = escalaY(meta);
  const yBaseline = baseline !== null ? escalaY(baseline) : null;

  return (
    <div className="relative">
      <svg width={LARGURA} height={ALTURA} role="img" aria-label="Série histórica do indicador">
        <line x1={PADDING} y1={yMeta} x2={LARGURA - PADDING} y2={yMeta} stroke="var(--cor-grafite)" strokeDasharray="2,2" strokeWidth={1} opacity={0.5} />
        {yBaseline !== null && (
          <line x1={PADDING} y1={yBaseline} x2={LARGURA - PADDING} y2={yBaseline} stroke="var(--cor-grafite)" strokeDasharray="1,3" strokeWidth={1} opacity={0.3} />
        )}
        <polyline points={pontos} fill="none" stroke="var(--cor-aco)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {medicoes.map((m, i) => (
          <circle
            key={m.id}
            cx={escalaX(i)}
            cy={escalaY(m.valor)}
            r={indiceHover === i ? 4 : 2.5}
            fill="var(--cor-aco)"
            stroke="var(--cor-superficie)"
            strokeWidth={1.5}
            onMouseEnter={() => setIndiceHover(i)}
            onMouseLeave={() => setIndiceHover(null)}
          />
        ))}
      </svg>
      {indiceHover !== null && (
        <div
          className="fonte-mono pointer-events-none absolute rounded border border-borda bg-superficie px-2 py-1 text-[11px] text-ink shadow"
          style={{ left: escalaX(indiceHover), top: -8 }}
        >
          {medicoes[indiceHover]!.valor} · {new Date(medicoes[indiceHover]!.calculadoEm).toLocaleDateString('pt-BR')}
        </div>
      )}
    </div>
  );
}
